import { useCallback, useMemo, useRef, useState } from 'react'

import { getConversationSummary } from '@/api/navigator'
import { useQuestion } from '@/hooks/useQuestion'
import { useUser } from '@/hooks/useUser'

import { useConsultation } from './useConsultation'
import { useConsultationType } from './useConsultationType'

type ConversationSummaryPrefill = {
  conversationId?: string | null
  isFetchingSummary: boolean
  updateSummary: () => Promise<void>
}

export const useConversationSummaryPrefill = (): ConversationSummaryPrefill => {
  const { question, updateQuestion, setAttachments } = useQuestion()
  const { user } = useUser()
  const { consultationType } = useConsultationType()
  const [isFetchingSummary, setIsFetchingSummary] = useState(false)
  const hasFetchedSummary = useRef(false)
  const { isFollowUp } = useConsultation()

  const conversationId = useMemo(() => new URLSearchParams(window.location.search).get('cid'), [])
  const uniqueId = user?.data?.uniqueId

  const shouldSkipPrefill = useMemo(
    () => Boolean(!conversationId || !uniqueId || question.question?.trim() || !consultationType || isFollowUp),
    [conversationId, uniqueId, question.question, consultationType, isFollowUp]
  )

  const updateSummary = useCallback(async () => {
    if (shouldSkipPrefill || hasFetchedSummary.current) return

    hasFetchedSummary.current = true
    setIsFetchingSummary(true)

    return getConversationSummary({ conversationId: conversationId!, uniqueId: uniqueId!, consultationType: consultationType! })
      .then(({ question, attachments }) => {
        updateQuestion(question)
        setAttachments(
          attachments.map((attachment) => ({
            file: new File([], ''),
            preview: attachment,
            url: attachment
          }))
        )
      })
      .catch((err) => {
        hasFetchedSummary.current = false
        console.error('Error fetching conversation summary', err)
      })
      .finally(() => {
        setIsFetchingSummary(false)
      })
  }, [conversationId, uniqueId, consultationType, shouldSkipPrefill])

  return {
    conversationId,
    isFetchingSummary,
    updateSummary
  }
}
